"use client";

// پنجره‌ی «راهنما و پشتیبانی»: چتِ ساده با پایگاهِ دانشِ پنل (/api/help/chat). با دکمه‌ی پایینِ
// ستونِ همکاران یا دکمه‌ی شناورِ گوشه‌ی صفحه باز می‌شود. گفتگو تا بسته شدنِ تب در
// sessionStorage می‌ماند.
import React, { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ArrowUpLeft, Check, Loader2, RotateCcw, Send, ThumbsDown, ThumbsUp, X } from "lucide-react";
import AvaAvatar from "./AvaAvatar";
import { HELP_PERSONA } from "@/lib/helpChat/persona";

type Link_ = { label: string; href: string };
type Suggestion = { id: string; title: string };
type ActionProposal = { id: string; confirmLabel: string };
type Message = {
  id: string; from: "bot" | "user"; text: string; links?: Link_[]; suggestions?: Suggestion[];
  /** شناسه‌ی گزارشِ همین جواب برای 👍/👎 */
  logId?: string; feedback?: "up" | "down";
  /** کارِ پیشنهادی که منتظرِ تأیید است */
  action?: ActionProposal;
};

export const HELP_CHAT_OPEN_EVENT = "help-chat:open";
export function openHelpChat() {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(HELP_CHAT_OPEN_EVENT));
}

const STORAGE_KEY = "help-chat:v1";
const uid = () => Math.random().toString(36).slice(2, 10);

function readStored(): { context: string; messages: Message[] } | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
function writeStored(context: string, messages: Message[]) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ context, messages: messages.slice(-40) }));
  } catch {
    /* حالتِ خصوصی یا ذخیره‌سازیِ بسته */
  }
}

/**
 * floating: «always» = دکمه‌ی شناور همیشه دیده می‌شود (صفحه‌های ورود)؛
 * «auto» = در پنل، روی صفحه‌ی پهن که ستونِ همکاران دکمه‌ی خودش را دارد، پنهان است.
 */
export default function HelpChatWidget({ floating = "always" }: { floating?: "always" | "auto" }) {
  const pathname = usePathname() || "/";
  const [open, setOpen] = useState(false);
  const [context, setContext] = useState<string>("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const start = useCallback(async (reset = false) => {
    setLoadError(false);
    try {
      const res = await fetch(`/api/help/chat?path=${encodeURIComponent(pathname)}`, { cache: "no-store", credentials: "include" });
      const data = await res.json();
      if (!res.ok || !data?.ok) throw new Error();
      const stored = reset ? null : readStored();
      // کلیدِ گفتگو = حالت (ورود/پنل) + بیننده؛ گفتگوی قبلی فقط برای همان حساب ادامه پیدا می‌کند
      const key = `${data.context}:${data.viewer || "guest"}`;
      setContext(key);
      if (stored && stored.context === key && stored.messages.length) {
        setMessages(stored.messages);
      } else {
        const first: Message[] = [{ id: uid(), from: "bot", text: data.greeting, suggestions: data.suggestions }];
        setMessages(first);
        writeStored(key, first);
      }
    } catch {
      setLoadError(true);
    }
  }, [pathname]);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(HELP_CHAT_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(HELP_CHAT_OPEN_EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (!open) return;
    if (!messages.length) void start();
    const t = window.setTimeout(() => inputRef.current?.focus(), 60);
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => { window.clearTimeout(t); window.removeEventListener("keydown", onKey); };
  }, [open, messages.length, start]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const ask = async (payload: { message?: string; entryId?: string; label?: string; confirmAction?: string; cancelAction?: string }, shownText: string) => {
    if (busy) return;
    const userMsg: Message = { id: uid(), from: "user", text: shownText };
    // پیشنهادها و دکمه‌های تأییدِ پیام‌های قبلی بعد از هر سؤال کنار می‌روند
    const next = [...messages.map((m) => ({ ...m, suggestions: undefined, action: undefined })), userMsg];
    setMessages(next);
    setBusy(true);
    try {
      const res = await fetch("/api/help/chat", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...payload, path: pathname }),
      });
      const data = await res.json().catch(() => ({}));
      const bot: Message = res.ok && data?.ok
        ? { id: uid(), from: "bot", text: data.reply.text, links: data.reply.links, suggestions: data.suggestions, logId: data.logId || undefined, action: data.reply.action || undefined }
        : { id: uid(), from: "bot", text: data?.error || "پاسخ آماده نشد؛ دوباره تلاش کنید." };
      const all = [...next, bot];
      setMessages(all);
      writeStored(context, all);
    } catch {
      setMessages([...next, { id: uid(), from: "bot", text: "اتصال برقرار نشد؛ اینترنت را بررسی و دوباره تلاش کنید." }]);
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  };

  // لینکِ فیش (?slip=) وقتی همین حالا روی همان صفحه‌ی فیش‌ها هستیم، ناوبری انجام نمی‌دهد؛
  // پس مستقیم به صفحه می‌گوییم همان فیش را باز کند.
  const openLink = (event: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    const target = new URL(href, window.location.origin);
    const slip = target.searchParams.get("slip");
    if (slip && target.pathname.replace(/\/+$/, "") === window.location.pathname.replace(/\/+$/, "")) {
      event.preventDefault();
      window.dispatchEvent(new CustomEvent("help-chat:open-slip", { detail: { id: slip } }));
    }
    if (window.innerWidth < 768 || slip) setOpen(false);
  };

  const sendFeedback = async (m: Message, value: "up" | "down") => {
    if (!m.logId || m.feedback) return;
    const updated = messages.map((x) => (x.id === m.id ? { ...x, feedback: value } : x));
    setMessages(updated);
    writeStored(context, updated);
    try {
      await fetch("/api/help/chat", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ feedback: { logId: m.logId, value }, path: pathname }) });
    } catch {
      /* نظر ثبت نشد؛ مهم نیست */
    }
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text) return;
    setInput("");
    void ask({ message: text }, text);
  };

  // دکمه‌ی شناور: چهره‌ی آوا با نشانِ «آنلاین»؛ روی صفحه‌ی پهن، کنارش «سؤالی دارید؟ از آوا بپرسید»
  const fabClass =
    "help-fab group fixed bottom-4 left-4 z-[60] inline-flex items-center gap-2.5 rounded-full bg-slate-900/95 p-1.5 text-slate-100 shadow-lg shadow-black/30 ring-1 ring-slate-700/80 backdrop-blur transition hover:-translate-y-0.5 hover:ring-brand-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-400 sm:pl-4" +
    (floating === "auto" ? " help-fab-auto" : "");

  return (
    <>
      {!open ? (
        <button type="button" className={fabClass} onClick={() => setOpen(true)} aria-label={`گفتگو با ${HELP_PERSONA.name}، ${HELP_PERSONA.role}`} title={`گفتگو با ${HELP_PERSONA.name}`}>
          <AvaAvatar className="h-12 w-12" online />
          <span className="hidden text-right leading-tight sm:block">
            <span className="block text-[13px] font-bold">سؤالی دارید؟</span>
            <span className="block text-[11px] text-slate-400 group-hover:text-brand-200">از {HELP_PERSONA.name} بپرسید</span>
          </span>
        </button>
      ) : null}

      {open ? (
        <section
          role="dialog"
          aria-label={`گفتگو با ${HELP_PERSONA.name}`}
          dir="rtl"
          className="fixed bottom-3 left-3 z-[70] flex h-[min(600px,calc(100dvh-1.5rem))] w-[min(400px,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-2xl bg-slate-900 text-slate-100 shadow-2xl shadow-black/40 ring-1 ring-slate-700/70"
        >
          <header className="flex items-center gap-3 bg-slate-950/60 px-4 py-3">
            <AvaAvatar className="h-11 w-11" online />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold">{HELP_PERSONA.name}</div>
              <div className="truncate text-[11px] text-slate-400">
                <span className="text-emerald-300">آنلاین</span> · {HELP_PERSONA.role}{context.startsWith("panel") ? "؛ طبقِ دسترسیِ شما" : ""}
              </div>
            </div>
            <button type="button" onClick={() => { setMessages([]); void start(true); }} className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 transition hover:bg-slate-800 hover:text-slate-100" title="شروع دوباره" aria-label="شروع دوباره گفتگو">
              <RotateCcw className="h-4 w-4" />
            </button>
            <button type="button" onClick={() => setOpen(false)} className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 transition hover:bg-slate-800 hover:text-slate-100" aria-label="بستن راهنما">
              <X className="h-5 w-5" />
            </button>
          </header>

          <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto px-3 py-4" aria-live="polite">
            {loadError ? (
              <div className="rounded-xl bg-rose-500/10 p-3 text-[13px] text-rose-200">
                راهنما بارگذاری نشد.{" "}
                <button type="button" className="font-semibold underline" onClick={() => void start(true)}>دوباره تلاش کنید</button>
              </div>
            ) : !messages.length ? (
              <div className="flex justify-center py-6 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
            ) : null}

            {messages.map((m) => (
              <div key={m.id} className={m.from === "user" ? "flex justify-end" : "flex items-start justify-start gap-2"}>
                {m.from === "bot" ? <AvaAvatar className="mt-0.5 h-7 w-7" /> : null}
                <div className={`max-w-[88%] ${m.from === "user" ? "rounded-2xl rounded-bl-md bg-brand-600 px-3.5 py-2.5 text-white" : "space-y-2"}`}>
                  {m.from === "user" ? (
                    <p className="whitespace-pre-line text-[13px] leading-6">{m.text}</p>
                  ) : (
                    <>
                      <div className="rounded-2xl rounded-br-md bg-slate-800 px-3.5 py-2.5">
                        <p className="whitespace-pre-line text-[13px] leading-7 text-slate-100">{m.text}</p>
                      </div>
                      {m.links?.length ? (
                        <div className="flex flex-wrap gap-1.5">
                          {m.links.map((l) =>
                            /^https?:\/\//.test(l.href) ? (
                              <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-lg bg-brand-500/15 px-2.5 py-1.5 text-[12px] font-semibold text-brand-200 transition hover:bg-brand-500/25">
                                {l.label} <ArrowUpLeft className="h-3.5 w-3.5" />
                              </a>
                            ) : (
                              <Link key={l.href} href={l.href} onClick={(event) => openLink(event, l.href)} className="inline-flex items-center gap-1 rounded-lg bg-brand-500/15 px-2.5 py-1.5 text-[12px] font-semibold text-brand-200 transition hover:bg-brand-500/25">
                                {l.label} <ArrowUpLeft className="h-3.5 w-3.5" />
                              </Link>
                            ),
                          )}
                        </div>
                      ) : null}
                      {m.action ? (
                        <div className="flex flex-wrap gap-1.5">
                          <button type="button" disabled={busy} onClick={() => void ask({ confirmAction: m.action!.id }, m.action!.confirmLabel)} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-[12px] font-bold text-white transition hover:bg-emerald-500 disabled:opacity-50">
                            <Check className="h-3.5 w-3.5" /> {m.action.confirmLabel}
                          </button>
                          <button type="button" disabled={busy} onClick={() => void ask({ cancelAction: m.action!.id }, "انصراف")} className="rounded-lg bg-slate-950/70 px-3 py-1.5 text-[12px] text-slate-200 ring-1 ring-slate-700 transition hover:bg-slate-800 disabled:opacity-50">
                            انصراف
                          </button>
                        </div>
                      ) : null}
                      {m.logId ? (
                        <div className="flex items-center gap-1 text-slate-500">
                          {m.feedback ? (
                            <span className="text-[11px]">{m.feedback === "up" ? "ممنون از نظرتان 🙏" : "ممنون؛ برای بهتر شدن جواب‌ها استفاده می‌شود."}</span>
                          ) : (
                            <>
                              <span className="text-[11px]">جواب مفید بود؟</span>
                              <button type="button" onClick={() => void sendFeedback(m, "up")} className="grid h-7 w-7 place-items-center rounded-md transition hover:bg-slate-800 hover:text-emerald-300" aria-label="جواب مفید بود"><ThumbsUp className="h-3.5 w-3.5" /></button>
                              <button type="button" onClick={() => void sendFeedback(m, "down")} className="grid h-7 w-7 place-items-center rounded-md transition hover:bg-slate-800 hover:text-rose-300" aria-label="جواب مفید نبود"><ThumbsDown className="h-3.5 w-3.5" /></button>
                            </>
                          )}
                        </div>
                      ) : null}
                      {m.suggestions?.length ? (
                        <div className="flex flex-col items-start gap-1.5 pt-0.5">
                          {m.suggestions.map((s) => (
                            <button key={s.id} type="button" disabled={busy} onClick={() => void ask({ entryId: s.id, label: s.title }, s.title)} className="rounded-xl bg-slate-950/70 px-3 py-1.5 text-right text-[12px] text-slate-200 ring-1 ring-slate-700 transition hover:bg-slate-800 hover:text-white disabled:opacity-50">
                              {s.title}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
              </div>
            ))}
            {busy ? (
              <div className="flex items-center justify-start gap-2">
                <AvaAvatar className="h-7 w-7" />
                <div className="rounded-2xl bg-slate-800 px-3.5 py-2.5 text-[12px] text-slate-400">{HELP_PERSONA.name} در حال نوشتن… <Loader2 className="inline h-3.5 w-3.5 animate-spin" /></div>
              </div>
            ) : null}
          </div>

          <form onSubmit={submit} className="flex items-center gap-2 bg-slate-950/60 p-3">
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value.slice(0, 500))}
              maxLength={500}
              placeholder={`پیام به ${HELP_PERSONA.name}…`}
              aria-label="سؤال"
              className="min-w-0 flex-1 rounded-xl bg-slate-900 px-3.5 py-2.5 text-[13px] text-slate-100 ring-1 ring-slate-700 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            {input.length > 400 ? <span className={`shrink-0 text-[11px] ${input.length >= 500 ? "text-rose-300" : "text-slate-400"}`} aria-live="polite">{(500 - input.length).toLocaleString("fa-IR")}</span> : null}
            <button type="submit" disabled={busy || !input.trim()} className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand-600 text-white transition hover:bg-brand-500 disabled:opacity-40" aria-label="ارسال سؤال">
              <Send className="h-4 w-4 -scale-x-100" />
            </button>
          </form>
        </section>
      ) : null}
    </>
  );
}
